import type { AnimEndActionNode } from "../../types.js";
import {
  BehaviorAnimationPayload,
  BehaviorFlow,
  ExecutionContext,
  NodeExecutionResult,
  PatchResult,
  RuntimeSignal,
  ScheduledNode,
} from "../types.js";
import { emptyPatchResult } from "../helpers.js";
import { BehaviorNode } from "./BehaviorNode.js";

export class AnimEndBehaviorNode extends BehaviorNode {
  declare behavior: AnimEndActionNode;

  constructor(behavior: AnimEndActionNode) {
    super(behavior);
  }

  execute(context: ExecutionContext): NodeExecutionResult {
    const patch: PatchResult = emptyPatchResult();
    const scheduledNodes: ScheduledNode[] = [];

    const draft = context.animationDraft;
    const begin = draft?.begin;
    const keyframes = draft?.keyframes ?? [];
    delete context.animationDraft;

    const sequenceOk = Boolean(begin && keyframes.length >= 1);

    let nextFlows: BehaviorFlow[] = [];
    const runtimeSignals: RuntimeSignal[] = [];

    if (sequenceOk && begin) {
      const payload: BehaviorAnimationPayload = {
        name: begin.name,
        durationMs: begin.durationMs,
        delayMs: begin.delayMs,
        iterationCount: begin.iterationCount,
        direction: begin.direction,
        fillMode: begin.fillMode,
        keyframes: keyframes.map((k) => ({
          offset: k.offset,
          easing: k.easing,
          values: { ...k.values },
        })),
      };
      patch.animation = payload;

      const waitMs = Math.max(
        0,
        begin.delayMs + begin.durationMs * begin.iterationCount,
      );
      const outEdges = this.outgoingEdgesByPort.get("out") ?? [];

      if (outEdges.length > 0) {
        for (const edge of outEdges) {
          scheduledNodes.push({
            at: context.nowMs + waitMs,
            kind: "edge",
            edge,
            context,
          });
        }
      } else if (context.loopStack.length > 0) {
        scheduledNodes.push({
          at: context.nowMs + waitMs,
          kind: "signal",
          signal: { type: "loop_continue", context },
          context,
        });
      }
    } else {
      nextFlows = (this.outgoingEdgesByPort.get("out") ?? []).map((edge) => ({
        edge,
        context,
      }));
      if (nextFlows.length === 0 && context.loopStack.length > 0) {
        runtimeSignals.push({ type: "loop_continue", context });
      }
    }

    return {
      patch,
      nextFlows,
      scheduledNodes,
      runtimeSignals,
    };
  }

  getOutput(
    _portId: string,
    _visiting: Set<string>,
    _context: ExecutionContext,
  ): unknown {
    return undefined;
  }
}
